import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import DashboardLayout from '../components/layout/Dashboardlayout.jsx';
import './Globalidentity.css';
import { gsap } from 'gsap';
import Loader from '../components/layout/preloader';

const GlobalIdentity = () => {
  const [navItems, setNavItems] = useState([]);
  const [footerLinks, setFooterLinks] = useState([]);
  const [footerTitles, setFooterTitles] = useState([]);

  const [modalType, setModalType] = useState(null);
  const [modalMode, setModalMode] = useState('edit');
  const [editRow, setEditRow] = useState(null);
  const [enVal, setEnVal] = useState('');
  const [arVal, setArVal] = useState('');
  const [loading, setLoading] = useState(false);

  const pageRef = useRef(null);
  const headerRef = useRef(null);
  const sectionsRef = useRef([]);
  const modalRef = useRef(null);
  const navRowsRef = useRef([]);
  const titleRowsRef = useRef([]);
  const linkRowsRef = useRef([]);

  const fetchAll = async () => {
    const { data: nav } = await supabase.from('nav_bar').select('*').order('id');
    const { data: links } = await supabase.from('Footer_links').select('*').order('id');
    const { data: titles } = await supabase.from('Footer_title').select('*').order('id');
    setNavItems(nav || []);
    setFooterLinks(links || []);
    setFooterTitles(titles || []);
  };

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' }, delay: 4 });

      tl.fromTo(
        headerRef.current,
        { opacity: 0, y: -22 },
        { opacity: 1, y: 0, duration: 0.6 }
      );

      tl.fromTo(
        sectionsRef.current.filter(Boolean),
        { opacity: 0, y: 26 },
        { opacity: 1, y: 0, duration: 0.55, stagger: 0.13 },
        '-=0.3'
      );
    }, pageRef);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const rows = navRowsRef.current.filter(Boolean);
    if (!rows.length) return;
    gsap.fromTo(rows, { opacity: 0, x: -14 }, { opacity: 1, x: 0, duration: 0.35, stagger: 0.05, ease: 'power2.out', delay: 4.2 });
  }, [navItems]);

  useEffect(() => {
    const rows = titleRowsRef.current.filter(Boolean);
    if (!rows.length) return;
    gsap.fromTo(rows, { opacity: 0, x: -14 }, { opacity: 1, x: 0, duration: 0.35, stagger: 0.05, ease: 'power2.out', delay: 4.2 });
  }, [footerTitles]);

  useEffect(() => {
    const rows = linkRowsRef.current.filter(Boolean);
    if (!rows.length) return;
    gsap.fromTo(rows, { opacity: 0, x: -14 }, { opacity: 1, x: 0, duration: 0.35, stagger: 0.05, ease: 'power2.out', delay: 4.2 });
  }, [footerLinks]);

  const animateModalIn = () => {
    if (!modalRef.current) return;
    gsap.fromTo(
      modalRef.current,
      { opacity: 0, scale: 0.94, y: 20 },
      { opacity: 1, scale: 1, y: 0, duration: 0.32, ease: 'power3.out' }
    );
  };

  const animateModalOut = (callback) => {
    if (!modalRef.current) { callback(); return; }
    gsap.to(modalRef.current, {
      opacity: 0, scale: 0.94, y: 14,
      duration: 0.2, ease: 'power2.in',
      onComplete: callback,
    });
  };

  useEffect(() => {
    if (modalType) setTimeout(() => animateModalIn(), 10);
  }, [modalType]);

  const openEdit = (type, row) => {
    setModalMode('edit');
    setModalType(type);
    setEditRow(row);
    if (type === 'nav') { setEnVal(row.nav_item_en || ''); setArVal(row.nav_item_ar || ''); }
    if (type === 'link') { setEnVal(row.footer_links_en || ''); setArVal(row.footer_links_ar || ''); }
    if (type === 'title') { setEnVal(row.category_en || ''); setArVal(row.category_ar || ''); }
  };

  const openAdd = (type) => {
    setModalMode('add');
    setModalType(type);
    setEditRow(null);
    setEnVal('');
    setArVal('');
  };

  const closeAll = () => {
    animateModalOut(() => {
      setModalType(null);
      setEditRow(null);
      setEnVal('');
      setArVal('');
    });
  };

  const handleSave = async () => {
    setLoading(true);
    if (modalMode === 'edit') {
      if (modalType === 'nav') await supabase.from('nav_bar').update({ nav_item_en: enVal, nav_item_ar: arVal }).eq('id', editRow.id);
      if (modalType === 'link') await supabase.from('Footer_links').update({ footer_links_en: enVal, footer_links_ar: arVal }).eq('id', editRow.id);
      if (modalType === 'title') await supabase.from('Footer_title').update({ category_en: enVal, category_ar: arVal }).eq('id', editRow.id);
    } else {
      if (modalType === 'nav') await supabase.from('nav_bar').insert([{ nav_item_en: enVal, nav_item_ar: arVal }]);
      if (modalType === 'link') await supabase.from('Footer_links').insert([{ footer_links_en: enVal, footer_links_ar: arVal }]);
      if (modalType === 'title') await supabase.from('Footer_title').insert([{ category_en: enVal, category_ar: arVal }]);
    }
    await fetchAll();
    closeAll();
    setLoading(false);
  };

  const deleteNav = async (id) => { await supabase.from('nav_bar').delete().eq('id', id); await fetchAll(); };
  const deleteLink = async (id) => { await supabase.from('Footer_links').delete().eq('id', id); await fetchAll(); };
  const deleteTitle = async (id) => { await supabase.from('Footer_title').delete().eq('id', id); await fetchAll(); };

  const modalTitles = {
    nav: { edit: 'EDIT NAV ITEM', add: 'ADD NAV ITEM' },
    link: { edit: 'EDIT FOOTER LINK', add: 'ADD FOOTER LINK' },
    title: { edit: 'EDIT FOOTER CATEGORY', add: 'ADD FOOTER CATEGORY' },
  };

  return (
    <>
      <Loader />
      <DashboardLayout activePath="/dashboard/navigation">
        <div className="gi-page" ref={pageRef}>

          <div className="gi-page-header" ref={headerRef}>
            <div>
              <h1 className="gi-page-title">GLOBAL IDENTITY</h1>
              <p className="gi-page-subtitle">Navigation Menu & Footer Content</p>
            </div>
          </div>

          <div className="gi-section" ref={(el) => (sectionsRef.current[0] = el)}>
            <div className="gi-section-header">
              <h2 className="gi-section-title">NAVIGATION MENU</h2>
              <button className="gi-btn-add" onClick={() => openAdd('nav')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Add Nav Item
              </button>
            </div>
            <div className="gi-table-wrapper">
              <table className="gi-table">
                <colgroup>
                  <col className="gi-col-main" />
                  <col className="gi-col-main" />
                  <col className="gi-col-actions" />
                </colgroup>
                <thead>
                  <tr>
                    <th>NAV ITEM (EN)</th>
                    <th>NAV ITEM (AR)</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {navItems.map((row, i) => (
                    <tr key={row.id} ref={(el) => (navRowsRef.current[i] = el)}>
                      <td>{row.nav_item_en}</td>
                      <td className="gi-ar gi-rtl">{row.nav_item_ar}</td>
                      <td>
                        <div className="gi-actions">
                          <button className="gi-action-btn gi-edit-btn" onClick={() => openEdit('nav', row)}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                          <button className="gi-action-btn gi-delete-btn" onClick={() => deleteNav(row.id)}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                              <path d="M10 11v6M14 11v6"/>
                              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="gi-section" ref={(el) => (sectionsRef.current[1] = el)}>
            <div className="gi-section-header">
              <h2 className="gi-section-title">FOOTER — CATEGORY TITLES</h2>
              <button className="gi-btn-add" onClick={() => openAdd('title')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Add Category
              </button>
            </div>
            <div className="gi-table-wrapper">
              <table className="gi-table">
                <colgroup>
                  <col className="gi-col-main" />
                  <col className="gi-col-main" />
                  <col className="gi-col-actions" />
                </colgroup>
                <thead>
                  <tr>
                    <th>CATEGORY (EN)</th>
                    <th>CATEGORY (AR)</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {footerTitles.map((row, i) => (
                    <tr key={row.id} ref={(el) => (titleRowsRef.current[i] = el)}>
                      <td>{row.category_en}</td>
                      <td className="gi-ar gi-rtl">{row.category_ar}</td>
                      <td>
                        <div className="gi-actions">
                          <button className="gi-action-btn gi-edit-btn" onClick={() => openEdit('title', row)}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                          <button className="gi-action-btn gi-delete-btn" onClick={() => deleteTitle(row.id)}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                              <path d="M10 11v6M14 11v6"/>
                              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="gi-section" ref={(el) => (sectionsRef.current[2] = el)}>
            <div className="gi-section-header">
              <h2 className="gi-section-title">FOOTER — LINKS</h2>
              <button className="gi-btn-add" onClick={() => openAdd('link')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Add Link
              </button>
            </div>
            <div className="gi-table-wrapper">
              <table className="gi-table">
                <colgroup>
                  <col className="gi-col-main" />
                  <col className="gi-col-main" />
                  <col className="gi-col-actions" />
                </colgroup>
                <thead>
                  <tr>
                    <th>LINK (EN)</th>
                    <th>LINK (AR)</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {footerLinks.map((row, i) => (
                    <tr key={row.id} ref={(el) => (linkRowsRef.current[i] = el)}>
                      <td>{row.footer_links_en}</td>
                      <td className="gi-ar gi-rtl">{row.footer_links_ar}</td>
                      <td>
                        <div className="gi-actions">
                          <button className="gi-action-btn gi-edit-btn" onClick={() => openEdit('link', row)}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                          <button className="gi-action-btn gi-delete-btn" onClick={() => deleteLink(row.id)}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                              <path d="M10 11v6M14 11v6"/>
                              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {modalType && (
            <div className="gi-modal-overlay" onClick={closeAll}>
              <div className="gi-modal" ref={modalRef} onClick={(e) => e.stopPropagation()}>
                <div className="gi-modal-header">
                  <h2 className="gi-modal-title">{modalTitles[modalType][modalMode]}</h2>
                  <button className="gi-modal-close" onClick={closeAll}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
                <div className="gi-modal-body">
                  <div className="gi-form-group">
                    <label>English (EN) <span className="gi-required">*</span></label>
                    <input type="text" value={enVal} onChange={(e) => setEnVal(e.target.value)} placeholder="English value" />
                  </div>
                  <div className="gi-form-group">
                    <label>Arabic (AR) <span className="gi-required">*</span></label>
                    <input className="gi-input-rtl" type="text" value={arVal} onChange={(e) => setArVal(e.target.value)} placeholder="القيمة بالعربية" />
                  </div>
                </div>
                <div className="gi-modal-footer">
                  <button className="gi-btn-cancel" onClick={closeAll}>Cancel</button>
                  <button className="gi-btn-confirm" onClick={handleSave} disabled={loading}>
                    {loading ? 'Saving...' : modalMode === 'add' ? 'Add' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </DashboardLayout>
    </>
  );
};

export default GlobalIdentity;